import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import Favorites from './pages/Favorites';
import AddRecipePage from './pages/AddRecipePage';
import EditRecipePage from './pages/EditRecipePage';
import PublicRecipeView from './pages/PublicRecipeView';
import ErrorPage from './pages/ErrorPage';
import ProfilePage from './pages/ProfilePage';
import { FavoritesProvider } from './context/FavoritesContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DisplayModeProvider } from './context/DisplayModeContext';
import { ToastProvider } from './context/ToastContext'; // Import ToastProvider
import FooterNav from './components/FooterNav';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import ScanMagnetMessage from './pages/ScanMagnetMessage';
import { isPwaMode } from './utils/tokenService'; // Import the token service
import HeaderNotifications from './components/HeaderNotifications';
import PwaLoginHandler from './pages/PwaLoginHandler'; // IMPORT PWA Login Handler
import AdminNotificationsPage from './pages/AdminNotificationsPage';
import useInAppNotifications from './hooks/notifications/useInAppNotifications';
import useNavbarHeight from './hooks/ui/useNavbarHeight';
import { TopNav } from './components/layout';
import './App.css'; // Global styles for App component

const AppErrorRoute: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search || '');
  const messageFromState =
    location.state && typeof location.state === 'object' && 'message' in location.state
      ? String(location.state.message || '')
      : '';
  const message = messageFromState || searchParams.get('message') || 'Une erreur est survenue.';

  return <ErrorPage message={message} />;
};

export const AppContent: React.FC = () => {
  const { author } = useAuth();
  const location = useLocation();
  const isPublicRecipePage = location.pathname.startsWith('/recipe/');
  const isUnauthorizedAccessPage = location.pathname === '/unauthorized-access';
  const shouldHideHeaderFooter = isPublicRecipePage || isUnauthorizedAccessPage || (isPwaMode() && !author);
  const { navbarHeight, navbarRef } = useNavbarHeight(!shouldHideHeaderFooter);
  const {
    notifications,
    notificationsLoading,
    unreadNotificationsCount,
    handleNotificationClick,
  } = useInAppNotifications(author);

  return (
    <>
      <a href="#main-content" className="skip-link">
        Aller au contenu principal
      </a>

      {!shouldHideHeaderFooter && (
        <TopNav ref={navbarRef} className="no-print app-desktop-shell">
            <div className="row w-100 align-items-center g-0">
              <div className="col-2">
                <Link to="/" className="nav-link" style={{ fontSize: '1.5rem' }} aria-label="Retour à l'accueil">
                  🏠
                </Link>
              </div>
              <div className="col-8 text-center">
                <Link className="navbar-brand" to="/" style={{ fontFamily: "'Pacifico', cursive" }}>La Marmite Familiale</Link>
              </div>
              <div className="col-2 text-end">
                {author ? (
                  <HeaderNotifications
                    notifications={notifications}
                    unreadCount={unreadNotificationsCount}
                    loading={notificationsLoading}
                    onNotificationClick={handleNotificationClick}
                  />
                ) : null}
              </div>
            </div>
        </TopNav>
      )}
      <main
        id="main-content"
        data-testid="app-container"
        className={shouldHideHeaderFooter ? "recipe-full-width-content" : "App-content app-desktop-shell app-shell-main"}
        style={{ paddingBottom: shouldHideHeaderFooter ? '0px' : '80px' }}
      >
        <Routes>
          <Route path="/" element={<ProtectedRoute><Home navbarHeight={navbarHeight} /></ProtectedRoute>} />
          <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
          <Route path="/add-recipe" element={<ProtectedRoute><AddRecipePage /></ProtectedRoute>} />
          <Route path="/edit-recipe/:id" element={<ProtectedRoute><EditRecipePage /></ProtectedRoute>} />
          <Route path="/recipe/:id" element={<PublicRecipeView />} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/admin/notifications" element={<AdminRoute><AdminNotificationsPage /></AdminRoute>} />
          <Route path="/unauthorized-access" element={<ScanMagnetMessage />} />
          <Route path="/pwa-login" element={<PwaLoginHandler />} /> {/* ADDED PWA LOGIN HANDLER ROUTE */}
          <Route path="/error" element={<AppErrorRoute />} />
          <Route path="*" element={<ErrorPage message="Page non trouvée." />} />
        </Routes>
      </main>
      {!shouldHideHeaderFooter && (
        <FooterNav className="no-print" />
      )}
    </>
  );
};

// Le composant App principal englobe les fournisseurs de contexte
function App() {
  return (
    <Router>
      <AuthProvider>
        <FavoritesProvider>
          <DisplayModeProvider>
            <ToastProvider>
              <AppContent />
            </ToastProvider>
          </DisplayModeProvider>
        </FavoritesProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
